import React, { useState, useEffect } from 'react';
import { Typography } from '../../interfaces/typography';
import { getServicesManager } from '../../services';

interface HubSpotIntegrationProps {
  apiKey: string;
}

interface HubSpotStatus {
  connected: boolean;
}

export function HubSpotIntegration({ apiKey }: HubSpotIntegrationProps) {
  const [status, setStatus] = useState<HubSpotStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [testContactId, setTestContactId] = useState('');
  const [testing, setTesting] = useState(false);
  const [testSuccess, setTestSuccess] = useState<string | null>(null);

  const services = getServicesManager();

  // Check HubSpot connection status
  const checkStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await services.getHubSpotOAuthStatus();
      if (data) {
        setStatus(data);
      } else {
        setStatus({ connected: false });
      }
    } catch (error) {
      console.error('Error checking HubSpot status:', error);
      setError(error instanceof Error ? error.message : 'Failed to check connection status');
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  };

  // Initiate OAuth flow
  const handleConnect = async () => {
    try {
      setError(null);
      const redirectUri = `${window.location.origin}/settings/integrations/hubspot/callback`;

      const data = await services.getHubSpotOAuthAuthorize(redirectUri);
      if (data) {
        // Redirect to HubSpot authorization page
        window.location.href = data.authorization_url;
      } else {
        throw new Error('Failed to get authorization URL');
      }
    } catch (error) {
      console.error('Error initiating HubSpot OAuth:', error);
      setError(error instanceof Error ? error.message : 'Failed to connect to HubSpot');
    }
  };

  // Disconnect HubSpot
  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect HubSpot? Call notes will no longer be synced automatically.')) {
      return;
    }

    try {
      setDisconnecting(true);
      setError(null);

      const result = await services.postHubSpotOAuthDisconnect();
      if (result?.success) {
        setStatus({ connected: false });
      } else {
        throw new Error('Failed to disconnect');
      }
    } catch (error) {
      console.error('Error disconnecting HubSpot:', error);
      setError(error instanceof Error ? error.message : 'Failed to disconnect from HubSpot');
    } finally {
      setDisconnecting(false);
    }
  };

  // Test integration by writing a fake call log
  const handleTestIntegration = async () => {
    if (!testContactId.trim()) {
      setError('Please enter a Contact ID');
      return;
    }

    try {
      setTesting(true);
      setError(null);
      setTestSuccess(null);

      const result = await services.testHubSpotIntegration(testContactId.trim());
      if (result?.success) {
        setTestSuccess(`Successfully created test call note for Contact ${testContactId}`);
        setTestContactId('');
      } else {
        throw new Error(result?.error || 'Failed to create test call note');
      }
    } catch (error) {
      console.error('Error testing HubSpot integration:', error);
      setError(error instanceof Error ? error.message : 'Failed to test integration');
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    if (apiKey) {
      checkStatus();
    }
  }, [apiKey]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="flex items-center gap-3">
          <svg className="animate-spin h-5 w-5 text-orange-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <Typography variant="mediumParagraph" color="#6B7280">
            Checking HubSpot connection...
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Error Message */}
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex flex-col gap-1">
            <Typography variant="mediumParagraph" color="#DC2626" fontWeight={600}>
              Error
            </Typography>
            <Typography variant="smallParagraph" color="#7F1D1D">
              {error}
            </Typography>
          </div>
        </div>
      )}

      {/* Connection Status */}
      <div className={`
        flex items-center justify-between p-6 rounded-lg border-l-4
        ${status?.connected
          ? 'bg-emerald-50 border-emerald-500'
          : 'bg-gray-50 border-gray-300'
        }
      `}>
        <div className="flex items-center gap-4">
          <div className={`
            w-12 h-12 rounded-full flex items-center justify-center
            ${status?.connected ? 'bg-emerald-100' : 'bg-gray-200'}
          `}>
            {status?.connected ? (
              <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-6 h-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <Typography
              variant="mediumParagraph"
              color={status?.connected ? "#059669" : "#6B7280"}
              fontWeight={600}
            >
              {status?.connected ? 'Connected' : 'Not Connected'}
            </Typography>
            <Typography variant="smallParagraph" color="#9CA3AF">
              {status?.connected
                ? 'Your HubSpot account is connected and call notes will sync automatically'
                : 'Connect your HubSpot account to sync call notes automatically'
              }
            </Typography>
          </div>
        </div>

        {status?.connected ? (
          <button
            onClick={handleDisconnect}
            disabled={disconnecting}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200 flex items-center gap-2"
          >
            {disconnecting ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={500}>
                  Disconnecting...
                </Typography>
              </>
            ) : (
              <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={500}>
                Disconnect
              </Typography>
            )}
          </button>
        ) : (
          <button
            onClick={handleConnect}
            className="px-6 py-3 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <Typography variant="mediumParagraph" color="#FFFFFF" fontWeight={600}>
              Connect to HubSpot
            </Typography>
          </button>
        )}
      </div>

      {/* Test Integration Section */}
      {status?.connected && (
        <div className="flex flex-col gap-4 p-6 bg-white border border-gray-200 rounded-lg">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-orange-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            <div className="flex-1 flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <Typography variant="mediumParagraph" color="#1F2937" fontWeight={600}>
                  Test Integration
                </Typography>
                <Typography variant="smallParagraph" color="#6B7280">
                  Verify your HubSpot integration by creating a test call note on a Contact record.
                </Typography>
              </div>

              {testSuccess && (
                <div className="flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <svg className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <Typography variant="smallParagraph" color="#059669">
                    {testSuccess}
                  </Typography>
                </div>
              )}

              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <Typography variant="smallParagraph" color="#374151" fontWeight={600}>
                    Contact ID
                  </Typography>
                  <input
                    type="text"
                    value={testContactId}
                    onChange={(e) => setTestContactId(e.target.value)}
                    placeholder="e.g., 12345"
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent font-mono text-sm"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && testContactId.trim()) {
                        handleTestIntegration();
                      }
                    }}
                  />
                </div>

                <button
                  onClick={handleTestIntegration}
                  disabled={testing || !testContactId.trim()}
                  className="px-6 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center"
                >
                  {testing ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={500}>
                        Testing...
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="smallParagraph" color="#FFFFFF" fontWeight={500}>
                      Test
                    </Typography>
                  )}
                </button>

                <Typography variant="smallParagraph" color="#9CA3AF" className="text-xs">
                  Tip: Find Contact IDs in HubSpot by opening the contact record and copying the ID from the URL.
                </Typography>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
